import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedPageLayout from '../components/SharedPageLayout';
import BrandName from '../components/BrandName';
import { ChevronDown, Package } from 'lucide-react';

export const FAQRoute = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "What file formats do you accept?",
      answer: "We accept all major 3D file formats including STL, OBJ, 3MF, PLY, AMF, and STEP files. Our system can also work with native CAD files from popular software like SolidWorks, Fusion 360, and AutoCAD."
    },
    {
      question: "How long does it take to complete an order?",
      answer: "Turnaround times vary based on complexity and quantity. Simple prototypes typically take 1-3 business days, while complex or high-quantity orders may take 1-2 weeks. You'll receive an estimated delivery time with your quote."
    },
    {
      question: "What materials do you offer?",
      answer: "We offer a wide range of materials including PLA, ABS, PETG, TPU, Wood-filled, Metal-filled filaments for FDM printing, and various resins for SLA printing. We also provide specialty materials for specific applications."
    },
    {
      question: "Do you offer design services?",
      answer: "Yes! Our experienced design team can help with everything from concept development to production-ready designs. We offer 2D to 3D conversion, design optimization, and custom engineering services."
    },
    {
      question: "What's your quality guarantee?",
      answer: "We maintain strict quality standards and inspect every part before shipping. If you're not satisfied with the quality, we'll reprint your order at no additional cost or provide a full refund."
    },
    {
      question: "How do you handle confidential projects?",
      answer: "We take intellectual property seriously. All projects are covered by strict confidentiality agreements, and we can sign additional NDAs as needed. Your files are securely stored and never shared."
    },
    {
      question: "What are your payment terms?",
      answer: "We accept all major credit cards and PayPal. Payment is required before production begins. For large orders or established business customers, we may offer net payment terms."
    },
    {
      question: "Do you ship internationally?",
      answer: "Currently, we ship within the United States and Canada. International shipping options are being developed and will be available soon."
    },
    {
      question: "Can I track my order?",
      answer: "Yes! You'll receive a tracking number once your order ships. You can also check your order status anytime by logging into your account and viewing your order history."
    },
    {
      question: "What if I need to make changes to my order?",
      answer: "Changes can be made before production begins. Once printing has started, changes may not be possible or may incur additional costs. Contact us as soon as possible if you need modifications."
    }
  ];

  return (
    <SharedPageLayout currentPage="faq">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_1d797660-bdd3-4967-b006-914724c6ec3a/artifacts/hgroungi_C3D%20other%20cover%20BG.jpg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-16 md:py-20">
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-4">Support</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-heading mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-blue-100">
                Find answers to common questions about our 3D printing services
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="border border-slate-200 rounded-2xl overflow-hidden bg-white hover:border-slate-300 transition-colors"
            >
              <button
                className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-slate-50 transition-colors"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <span className="font-semibold text-slate-900">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-slate-400 transform transition-transform duration-300 ${
                  openFaq === index ? 'rotate-180' : ''
                }`} />
              </button>
              {openFaq === index && (
                <div className="px-6 pb-5">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 bg-slate-50 rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Still Have Questions?</h2>
          <p className="text-slate-600 mb-8 text-lg">
            Can&apos;t find what you&apos;re looking for? Our team is here to help.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-[#0007b5] hover:bg-[#000690] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-blue-900/20"
          >
            Contact Us
          </button>
        </div>
      </div>
    </SharedPageLayout>
  );
};

export const TermsRoute = () => {
  return (
    <SharedPageLayout currentPage="terms">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 mb-8">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 mb-4">
              By accessing and using <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> services, you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Services</h2>
            <p className="text-slate-700 mb-4">
              <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> provides 3D printing, design, and manufacturing services. We reserve the right to 
              modify or discontinue any service at any time without notice.
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>3D printing and prototyping services</li>
              <li>Design and engineering consulting</li>
              <li>File conversion and optimization</li>
              <li>Manufacturing and production support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. User Responsibilities</h2>
            <p className="text-slate-700 mb-4">Users are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Ensuring they have legal rights to all submitted designs and files</li>
              <li>Providing accurate project specifications and requirements</li>
              <li>Complying with all applicable laws and regulations</li>
              <li>Not submitting designs for illegal, harmful, or offensive items</li>
              <li>Respecting intellectual property rights of others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Intellectual Property</h2>
            <p className="text-slate-700 mb-4">
              You retain all rights to your original designs and files. <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span> does not claim ownership 
              of customer-submitted content. However, by submitting files, you grant us a limited license to 
              process and manufacture your designs as part of our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Payment Terms</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>All prices are quoted in USD unless otherwise specified</li>
              <li>Payment is required before production begins</li>
              <li>We accept major credit cards and PayPal</li>
              <li>Pricing may vary based on material costs and project complexity</li>
              <li>Rush orders may incur additional fees</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibent text-slate-900 mb-4">6. Quality and Delivery</h2>
            <p className="text-slate-700 mb-4">
              We strive to deliver high-quality products within the specified timeframe. However:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              <li>Delivery times are estimates and may vary due to production complexity</li>
              <li>We inspect all products before shipping but cannot guarantee perfection</li>
              <li>Customer approval may be required for certain design modifications</li>
              <li>We will remake defective parts at no additional cost when fault lies with our production</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Confidentiality</h2>
            <p className="text-slate-700 mb-4">
              We understand the importance of protecting your intellectual property. All customer files and 
              project information are treated as confidential and are not shared with third parties without 
              explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-700 mb-4">
              <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>&apos; liability is limited to the cost of the specific service provided. We are not 
              responsible for indirect, consequential, or incidental damages arising from our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Contact Information</h2>
            <p className="text-slate-700 mb-4">
              For questions about these Terms of Service, please contact us through our website or customer service.
            </p>
          </section>

          <div className="bg-slate-100 rounded-lg p-6 mt-8">
            <p className="text-slate-700 text-sm">
              These terms are subject to change without notice. Continued use of our services constitutes 
              acceptance of any modifications to these terms.
            </p>
          </div>
        </div>
      </div>
    </SharedPageLayout>
  );
};

export const FileFormatsRoute = () => {
  const fileFormats = [
    {
      format: "STEP/STP",
      description: "CAD format preserving parametric geometry",
      compatibility: "Good",
      recommended: false,
      ideal: true,
      details: "Best for engineering parts requiring precise dimensions or continuous, smooth curvatures."
    },
    {
      format: "STL",
      description: "Standard format for 3D printing, contains mesh geometry",
      compatibility: "Excellent",
      recommended: true,
      details: "Most common 3D printing format. Works with all our printing technologies."
    },
    {
      format: "OBJ", 
      description: "Simple 3D geometry format with optional texture support",
      compatibility: "Excellent",
      recommended: true,
      details: "Great for models with textures or complex geometries."
    },
    {
      format: "3MF",
      description: "Microsoft 3D Manufacturing Format with rich metadata",
      compatibility: "Excellent", 
      recommended: true,
      details: "Modern format that preserves materials, colors, and print settings."
    },
    {
      format: "PLY",
      description: "Polygon file format for storing 3D scan data",
      compatibility: "Good",
      recommended: false,
      details: "Useful for scanned objects and research applications."
    },
    {
      format: "AMF",
      description: "Additive Manufacturing File format with color support",
      compatibility: "Good",
      recommended: false,
      details: "Advanced format supporting multiple materials and colors."
    }
  ];

  return (
    <SharedPageLayout currentPage="file-formats">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_1d797660-bdd3-4967-b006-914724c6ec3a/artifacts/hgroungi_C3D%20other%20cover%20BG.jpg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-16 md:py-20">
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-4">Technical Resources</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-heading mb-4">
                Supported File Formats
              </h1>
              <p className="text-lg text-blue-100 max-w-3xl mx-auto">
                We support all major 3D file formats to ensure compatibility with your design workflow
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* File Formats Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-16">
          {fileFormats.map((format, index) => (
            <div key={index} className={`rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
              format.ideal 
                ? 'border-2 border-[#0007b5]/30 bg-[#0007b5]/5 md:col-span-2 lg:col-span-1 lg:row-span-1 lg:p-8' 
                : format.recommended 
                  ? 'border-2 border-green-200 bg-green-50' 
                  : 'border border-slate-200 bg-white'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-bold text-slate-900 ${format.ideal ? 'text-2xl' : 'text-xl'}`}>.{format.format}</h3>
                {format.ideal && (
                  <span className="bg-[#0007b5] text-white text-xs px-3 py-1 rounded-full font-medium">
                    Ideal
                  </span>
                )}
                {format.recommended && !format.ideal && (
                  <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Recommended
                  </span>
                )}
              </div>
              <p className={`text-slate-700 mb-4 ${format.ideal ? 'text-base' : ''}`}>{format.description}</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Compatibility:</span>
                  <span className={`text-sm font-semibold ${
                    format.compatibility === 'Excellent' ? 'text-green-600' : 'text-[#0007b5]'
                  }`}>
                    {format.compatibility}
                  </span>
                </div>
                <p className={`text-slate-600 ${format.ideal ? 'text-base' : 'text-sm'}`}>{format.details}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Guidelines */}
        <div className="bg-slate-50 rounded-2xl p-10 mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">File Preparation Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-4">✅ Best Practices</h3>
              <ul className="space-y-3 text-slate-700">
                <li>• Use STL or 3MF for best compatibility</li>
                <li>• Ensure models are manifold (watertight)</li>
                <li>• Include proper wall thickness (min 0.8mm)</li>
                <li>• Orient models for optimal printing</li>
                <li>• Check for intersecting geometries</li>
                <li>• Keep file sizes reasonable (&lt;50MB)</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-4">⚠️ Common Issues</h3>
              <ul className="space-y-3 text-slate-700">
                <li>• Non-manifold geometry</li>
                <li>• Holes or gaps in mesh</li>
                <li>• Inverted normals</li>
                <li>• Too thin walls (&lt;0.4mm)</li>
                <li>• Unsupported overhangs</li>
                <li>• Corrupted or incomplete files</li>
              </ul>
            </div>
          </div>
        </div>

        {/* File Size Limits */}
        <div className="bg-blue-50 rounded-2xl p-8 mb-10">
          <h3 className="text-lg font-bold text-slate-900 mb-6 text-center">File Size & Upload Limits</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold text-[#0007b5] mb-2">50MB</div>
              <div className="text-sm text-slate-600 font-medium">Max file size</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold text-[#0007b5] mb-2">10</div>
              <div className="text-sm text-slate-600 font-medium">Files per upload</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="text-3xl font-bold text-[#0007b5] mb-2">24/7</div>
              <div className="text-sm text-slate-600 font-medium">Upload availability</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Upload Your Files?</h2>
          <p className="text-slate-600 mb-8 text-lg">
            Start your project today and get an instant quote for your 3D printing needs
          </p>
          <a
            href="/"
            className="inline-block bg-[#0007b5] hover:bg-[#000690] text-white px-10 py-4 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-blue-900/20"
          >
            Get Started
          </a>
        </div>
      </div>
    </SharedPageLayout>
  );
};

export const MaterialGuideRoute = () => {
  const navigate = useNavigate();
  
  return (
    <SharedPageLayout currentPage="material-guide">
      {/* Hero Banner - Background Image with Blue Overlay */}
      <div className="relative text-white overflow-hidden">
        
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_1d797660-bdd3-4967-b006-914724c6ec3a/artifacts/hgroungi_C3D%20other%20cover%20BG.jpg)' }}
        ></div>
        
        {/* Blue overlay to match brand colors */}
        <div className="absolute inset-0 bg-[#0007b5]/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-16 md:py-20">
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-blue-200 text-sm font-medium tracking-widest uppercase mb-4">Technical Resources</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-heading mb-4">
                Material Guide
              </h1>
              <p className="text-lg text-blue-100 max-w-3xl mx-auto">
                Comprehensive guide to 3D printing materials, their properties, and best applications
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-lg text-slate-600 mb-8">
          This guide covers key materials, including their strengths, weaknesses, pros and cons for various applications, 
          and documented mechanical properties from industry testing (e.g., tensile strength, impact resistance, heat deflection temperature or HDT). 
          Data is sourced from reliable benchmarks like UltiMaker, Formlabs, and filament manufacturers. We focus on FDM/FFF printing, 
          where these materials excel. <button 
            onClick={() => navigate('/contact')} 
            className="font-semibold text-[#0007b5] hover:text-blue-700 underline cursor-pointer"
          >
            Consult our team for custom advice or material samples.
          </button>
        </p>

        <div className="space-y-12">
          {/* PLA Section */}
          <section className="border-l-4 border-green-500 pl-6 bg-green-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PLA (Polylactic Acid) and Variants</h2>
            <p className="text-slate-700 mb-4">
              PLA is a biodegradable thermoplastic derived from renewable resources like cornstarch, making it the most beginner-friendly filament.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Easy to print with low warping and no need for an enclosure or heated bed</li>
                  <li>• Affordable, odorless (mild sweet smell), and non-toxic (food-safe in raw form)</li>
                  <li>• Excellent detail and surface finish; eco-friendly</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Brittle with low impact resistance; deforms under heat or UV exposure</li>
                  <li>• Not suitable for high-stress or outdoor use</li>
                  <li>• Requires industrial composting to biodegrade</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Prototypes, decorative items, figurines, hobby projects, and low-stress parts like cookie cutters.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Functional parts in heat (e.g., car interiors) or mechanical loads.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard PLA)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 50-60 MPa</div>
                <div><strong>Impact Resistance:</strong> Low</div>
                <div><strong>Elongation:</strong> 3-5%</div>
                <div><strong>HDT:</strong> ~55-60°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> Low</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PLA+ (Tough PLA)</h4>
                <p className="text-sm text-slate-700">Enhanced impact resistance (tensile 60-65 MPa) and heat tolerance (~60-70°C); Less brittle but slight stringing. <strong>Uses:</strong> Functional prototypes.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">Silk PLA</h4>
                <p className="text-sm text-slate-700">Glossy, aesthetic finish that hides layer lines; Softer/weaker (tensile 30-50 MPa). <strong>Uses:</strong> Decorative prints.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">Wood/Metal-Filled PLA</h4>
                <p className="text-sm text-slate-700">Textured appearance; Sandable/polishable but abrasive (requires hardened nozzle), brittle. <strong>Uses:</strong> Artistic models.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CF PLA (Carbon Fiber PLA)</h4>
                <p className="text-sm text-slate-700 mb-2">Reinforced with 10-15% carbon fibers for added rigidity. Higher stiffness, strength, and matte finish that masks layers. Abrasive (needs hardened nozzle), potentially brittle, higher cost.</p>
                <p className="text-xs text-slate-600"><strong>Mechanical:</strong> Tensile 50-70 MPa, HDT ~55-65°C, elongation 2-4%. <strong>Uses:</strong> Functional prototypes, gears, brackets, load-bearing parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">HT PLA (High Temperature PLA)</h4>
                <p className="text-sm text-slate-700 mb-2">Formulated for better heat resistance, often requiring annealing. Improved HDT and impact (3x standard PLA in some tests), maintains print ease. May have lower Z-axis strength (~43% less in some variants).</p>
                <p className="text-xs text-slate-600"><strong>Mechanical:</strong> Tensile 45-55 MPa, HDT ~70-100°C (post-annealing at 100°C for 30 min), elongation 3-5%. <strong>Uses:</strong> Heat-exposed prototypes, enclosures, functional parts.</p>
              </div>
            </div>
          </section>

          {/* PETG Section */}
          <section className="border-l-4 border-blue-500 pl-6 bg-blue-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PETG (Polyethylene Terephthalate Glycol) and Variants</h2>
            <p className="text-slate-700 mb-4">
              PETG is a durable, glycol-modified version of PET (bottle plastic), offering a balance of strength and ease.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Tough with good flexibility and impact absorption</li>
                  <li>• Chemically and UV resistant</li>
                  <li>• Minimal warping, recyclable, and food-safe</li>
                  <li>• Strong layer adhesion for watertight prints</li>
                  <li>• Versatile for indoor/outdoor use; low odor</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Prone to stringing and oozing (needs tuning)</li>
                  <li>• Can damage brass nozzles over time</li>
                  <li>• Moderately moisture-sensitive</li>
                  <li>• Sticks aggressively to print beds (use release agents)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Functional parts like brackets, enclosures, containers, prosthetics, and vase-mode prints.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Extreme heat applications (e.g., engine parts) or abrasive environments.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard PETG)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 40-60 MPa</div>
                <div><strong>Impact Resistance:</strong> Medium</div>
                <div><strong>Elongation:</strong> 20-30%</div>
                <div><strong>HDT:</strong> ~70-80°C</div>
                <div><strong>Layer Adhesion:</strong> High</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PCTG</h4>
                <p className="text-sm text-slate-700">Higher impact (20% more than PETG); More flexible, less warping. Similar stringing. <strong>Uses:</strong> Impact-prone parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CPE/CPE+</h4>
                <p className="text-sm text-slate-700">Similar to PETG but higher heat (~100°C); Stronger adhesion but more expensive. <strong>Uses:</strong> Electronics housings.</p>
              </div>
            </div>
          </section>

          {/* ABS Section */}
          <section className="border-l-4 border-orange-500 pl-6 bg-orange-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">ABS (Acrylonitrile Butadiene Styrene) and Variants</h2>
            <p className="text-slate-700 mb-4">
              ABS is a tough engineering plastic used in LEGO and automotive parts, known for durability.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• High impact and heat resistance</li>
                  <li>• Flexible without brittleness</li>
                  <li>• Can be smoothed with acetone for glossy finish</li>
                  <li>• Affordable and gluable</li>
                  <li>• Good for post-processing like painting or sanding</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Significant warping (requires heated bed/enclosure)</li>
                  <li>• Emits strong fumes (needs ventilation)</li>
                  <li>• UV-unstable (degrades outdoors)</li>
                  <li>• Not food-safe</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Functional prototypes, tool handles, enclosures, drone frames, and automotive clips.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Food contact or prolonged sun exposure without coatings.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties (Standard ABS)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 34-40 MPa</div>
                <div><strong>Impact Resistance:</strong> High</div>
                <div><strong>Elongation:</strong> 20-30%</div>
                <div><strong>HDT:</strong> ~98-100°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> Medium</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">ABS+</h4>
                <p className="text-sm text-slate-700">Reduced warping (~50% less) and odor; Flame-retardant options. Similar fumes. <strong>Uses:</strong> Safety-critical parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">ASA</h4>
                <p className="text-sm text-slate-700">UV/weather-resistant; Matte finish, milder odor. Slightly higher cost. HDT ~105°C. <strong>Uses:</strong> Outdoor applications.</p>
              </div>
            </div>
          </section>

          {/* TPU Section */}
          <section className="border-l-4 border-purple-500 pl-6 bg-purple-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">TPU (Thermoplastic Polyurethane)</h2>
            <p className="text-slate-700 mb-4">
              TPU is a flexible, rubber-like elastomer ideal for elastic parts.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Highly flexible and stretchable</li>
                  <li>• Excellent impact and abrasion resistance</li>
                  <li>• Durable with vibration dampening</li>
                  <li>• Low warping and chemical resistance</li>
                  <li>• Good for skin-safe applications</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Difficult to print (slow speeds, direct-drive extruder needed)</li>
                  <li>• Prone to stringing and jams</li>
                  <li>• Highly moisture-sensitive (causes popping)</li>
                  <li>• Soft, deforms under weight</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Phone cases, gaskets, tires, grips, wearables, and bumpers.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Rigid structural parts or high-precision prints.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 20-40 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> &gt;500%</div>
                <div><strong>HDT:</strong> ~60-80°C</div>
                <div><strong>Shore Hardness:</strong> 85A-98A</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">TPU 85A (Ultra-Flexible)</h4>
                <p className="text-sm text-slate-700">Very soft; High stretch. Prints slowly (10-20 mm/s). <strong>Uses:</strong> Seals.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">TPU 64D</h4>
                <p className="text-sm text-slate-700">Semi-rigid; Easier to print. Less flexible. <strong>Uses:</strong> Structural flex parts.</p>
              </div>
            </div>
          </section>

          {/* PC Section */}
          <section className="border-l-4 border-indigo-500 pl-6 bg-indigo-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PC (Polycarbonate)</h2>
            <p className="text-slate-700 mb-4">
              PC is a high-performance engineering plastic known for transparency and toughness.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Extremely strong, stiff, and heat-resistant</li>
                  <li>• Lightweight in composites</li>
                  <li>• Impact-resistant (bulletproof glass material)</li>
                  <li>• Transparent options</li>
                  <li>• Good for high-temp and mechanical stress</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Challenging to print (high warping, needs enclosure and high temps ~270-290°C)</li>
                  <li>• Expensive</li>
                  <li>• Abrasive in filled variants (requires hardened nozzle)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">High-heat prototypes, molds, prosthetics, and engineering parts.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Beginner prints or low-temp environments.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 50-70 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> 50-100%</div>
                <div><strong>HDT:</strong> ~150°C</div>
                <div><strong>Layer Adhesion:</strong> Medium</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PC-CF/PC-GF (Carbon/Glass Fiber)</h4>
                <p className="text-sm text-slate-700">Stiffer, less warping; Heat ~120°C. Brittle in CF. <strong>Uses:</strong> Aerospace-like parts.</p>
              </div>
            </div>
          </section>

          {/* PA Section */}
          <section className="border-l-4 border-teal-500 pl-6 bg-teal-50/50 py-6 rounded-r-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">PA (Polyamide/Nylon)</h2>
            <p className="text-slate-700 mb-4">
              PA, or Nylon, is a strong, semi-flexible synthetic polymer used in textiles and gears.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-green-700 mb-3 flex items-center">
                  <span className="mr-2">✅</span> Pros
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Exceptional toughness and impact resistance</li>
                  <li>• Self-lubricating with low friction</li>
                  <li>• Heat and chemical resistant</li>
                  <li>• Bends without breaking</li>
                  <li>• Ideal for wear-resistant applications</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <span className="mr-2">⚠️</span> Cons
                </h3>
                <ul className="space-y-2 text-slate-700">
                  <li>• Extremely hygroscopic (must dry before printing)</li>
                  <li>• Warps easily (needs enclosure)</li>
                  <li>• Difficult to print (high temps ~240-270°C)</li>
                  <li>• Dimensional instability in humidity</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Best Uses</h3>
              <p className="text-slate-700 mb-2">Gears, bushings, tools, prosthetics, drone propellers, and mechanical parts.</p>
              <p className="text-sm text-red-700"><strong>Avoid:</strong> Humid environments without sealing or non-functional prints.</p>
            </div>

            <div className="bg-slate-100 rounded-lg p-5 mb-4">
              <h3 className="font-bold text-slate-900 mb-3">Mechanical Properties</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Tensile Strength:</strong> 40-80 MPa</div>
                <div><strong>Impact Resistance:</strong> Very high</div>
                <div><strong>Elongation:</strong> 20-50%</div>
                <div><strong>HDT:</strong> ~50-100°C</div>
                <div><strong>Layer Adhesion:</strong> High</div>
                <div><strong>Chemical Resistance:</strong> High</div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-slate-900 text-lg">Variants</h3>
              
              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PA6/PA66</h4>
                <p className="text-sm text-slate-700">Very tough; High impact. High moisture/warp. <strong>Uses:</strong> Heavy-duty parts.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">PA12</h4>
                <p className="text-sm text-slate-700">Easier print, lower moisture; Balanced. Slightly less strong. <strong>Uses:</strong> General engineering.</p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <h4 className="font-semibold text-slate-900">CF/GF Nylon</h4>
                <p className="text-sm text-slate-700">Stiffer, heat ~120°C+; Impact-resistant. Abrasive. <strong>Uses:</strong> Structural components.</p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 bg-[#0007b5] rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Ready to Choose Your Material?</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Our team can help you select the perfect material for your project based on your specific requirements and application.
          </p>
          <a 
            href="/" 
            className="inline-block bg-white text-[#0007b5] hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
          >
            Start Your Project
          </a>
        </div>
      </div>
    </SharedPageLayout>
  );
};

export const QualityStandardsRoute = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                <BrandName size="md" />
              </h1>
            </button>
            <button onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-800 transition-colors">← Back to Home</button>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Quality Standards</h1>
        <div className="space-y-8">
          <section className="bg-green-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-green-900 mb-4">Our Quality Promise</h2>
            <p className="text-green-800">Every part we produce meets or exceeds industry standards for precision, surface finish, and dimensional accuracy.</p>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Precision Standards</h2>
            <ul className="space-y-2 text-slate-700">
              <li>• Dimensional accuracy: ±0.1mm for critical features</li>
              <li>• Layer height: 0.1mm to 0.3mm options</li>
              <li>• Surface finish: Ra 1.6-6.3 μm depending on process</li>
              <li>• Material consistency: &lt;2% variance in properties</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Quality Control Process</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Pre-Production</h3>
                <p className="text-slate-700">File analysis, printability assessment, and optimization recommendations.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">During Production</h3>
                <p className="text-slate-700">Real-time monitoring, temperature control, and process verification.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Post-Production</h3>
                <p className="text-slate-700">Visual inspection, dimensional verification, and surface finish assessment.</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Packaging</h3>
                <p className="text-slate-700">Protective packaging and detailed quality documentation included.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export const ShippingInfoRoute = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="bg-gradient-to-r from-[#0007b5] to-[#000690] bg-clip-text text-transparent">
                <BrandName size="md" />
              </h1>
            </button>
            <button onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-800 transition-colors">← Back to Home</button>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Shipping & Returns</h1>
        <div className="space-y-8">
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">Shipping Options</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Standard Shipping</h3>
                <p className="text-slate-700 mb-2">5-7 business days • FREE on orders over $50</p>
                <p className="text-sm text-slate-600">Perfect for non-urgent orders</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Express Shipping</h3>
                <p className="text-slate-700 mb-2">2-3 business days • $15.99</p>
                <p className="text-sm text-slate-600">When you need it fast</p>
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Coverage Areas</h2>
            <ul className="space-y-2 text-slate-700">
              <li>• United States: All 50 states</li>
              <li>• Canada: All provinces and territories</li>
              <li>• International shipping: Coming soon</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Packaging & Handling</h2>
            <p className="text-slate-700 mb-4">
              All orders are carefully packaged with protective materials to ensure your parts arrive in perfect condition. 
              We use recyclable packaging materials whenever possible.
            </p>
            <ul className="space-y-2 text-slate-700">
              <li>• Bubble wrap protection for delicate parts</li>
              <li>• Rigid boxes for dimensional stability</li>
              <li>• Detailed packing slips included</li>
              <li>• Tracking information provided</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};
